import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Space,
  Avatar,
  Dropdown,
  Typography,
  theme,
} from 'antd';
import {
  UserOutlined,
  DashboardOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShoppingCartOutlined,
  MonitorOutlined,
  PhoneOutlined,
  DollarOutlined,
  FolderOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { ORDER_SERVICE_ACCESS_LABELS, ORDER_SERVICE_ACCESS_OPTIONS } from '../config/order-service-access';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { state, logout, hasPermission } = useAuth();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const app_display_name = ['agent.pinkeman.com', 'order-en.pinkeman.com'].includes(window.location.hostname)
    ? 'PINKEMAN OMS'
    : '平克曼业务管理系统';
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  // 获取当前选中的菜单键
  const getSelectedKeys = (): string[] => {
    const path = location.pathname;
    const search = location.search;

    // 对于订单页面，需要根据 service 参数确定选中项
    if (path === '/orders') {
      const params = new URLSearchParams(search);
      const service = params.get('service');
      if (service) {
        return [`/orders?service=${service}`];
      }
      // 没有 service 参数时，默认选中 "所有订单"
      return ['/orders?service=all'];
    }

    // 其他页面直接使用 pathname
    return [path];
  };

  const orderMenuChildren = ORDER_SERVICE_ACCESS_OPTIONS
    .filter(option => permissions.hasOrderServiceAccess(option.key))
    .map(option => ({
      key: `/orders?service=${option.key}`,
      label: option.label,
    }));

  // Menu items based on permissions
  const menuItems = [
    ...((!state.roles.includes('client') && !state.roles.includes('peers')) ? [{
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    }] : []),
    ...((permissions.hasPageAccess('order-management') && orderMenuChildren.length > 0) ? [{
      key: 'orders',
      icon: <ShoppingCartOutlined />,
      label: '业务订单',
      children: orderMenuChildren,
    }] : []),
    ...((hasPermission('page.trademark-monitor.view') || hasPermission('page.status-translation.view')) ? [{
      key: 'trademark',
      icon: <MonitorOutlined />,
      label: '商标监控',
      children: [
        ...(hasPermission('page.trademark-monitor.view') ? [{
          key: '/trademark-monitor',
          label: '商标监控',
        }] : []),
        ...(hasPermission('page.status-translation.view') ? [{
          key: '/status-translation',
          label: '状态翻译',
        }] : []),
      ],
    }] : []),
    ...(hasPermission('page.follow-up-management.view') ? [{
      key: '/follow-ups',
      icon: <PhoneOutlined />,
      label: '客户跟进',
    }] : []),
    ...(hasPermission('page.fee-management.view') ? [{
      key: '/fees',
      icon: <DollarOutlined />,
      label: '最新报价',
    }] : []),
    ...((hasPermission('page.patent-payment-management.view') || hasPermission('page.fee-management.view')) ? [{
      key: '/patent-payments',
      icon: <DollarOutlined />,
      label: '专利年费监控',
    }] : []),
    ...(hasPermission('page.document-management.view') ? [{
      key: '/documents',
      icon: <FolderOutlined />,
      label: '文档管理',
    }] : []),
    ...(hasPermission('user.view') ? [{
      key: '/media-library',
      icon: <PictureOutlined />,
      label: '媒体库',
    }] : []),
    // 用户管理分组 - 至少包含个人设置，所有认证用户都能看到
    ...(state.authenticated ? [{
      key: 'user-management',
      icon: <UserOutlined />,
      label: '用户',
      children: [
        ...(hasPermission('user.view') ? [{
          key: '/users',
          label: '用户管理',
        }] : []),
        ...(hasPermission('role.view') ? [{
          key: '/roles',
          label: '角色权限',
        }] : []),
        {
          key: '/profile',
          label: '个人设置',
        },
      ],
    }] : []),
    // 设置分组
    ...(hasPermission('system.manage') ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      children: [
        {
          key: '/notifications',
          label: '通知模板',
        },
        {
          key: '/data-settings',
          label: '数据设置',
        },
        // {
        //   key: '/schedule-tasks',
        //   label: '定时任务',
        // },
        ...(hasPermission('system.view') ? [{
          key: '/system-logs',
          label: '系统日志',
        }] : []),
      ],
    }] : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // 获取当前页面标题
  const getPageTitle = () => {
    const path = location.pathname;
    const search = location.search;

    // 页面标题映射
    const titleMap: Record<string, string> = {
      '/dashboard': '仪表板',
      '/orders': getOrdersTitle(search),
      '/trademark-monitor': '商标监控',
      '/status-translation': '状态翻译',
      '/follow-ups': '客户跟进',
      '/fees': '最新报价',
      '/documents': '文档管理',
      '/patent-payments': '专利年费监控',
      '/media-library': '媒体库',
      '/users': '用户管理',
      '/roles': '角色权限',
      '/notifications': '通知模板',
      '/data-settings': '数据设置',
      '/schedule-tasks': '定时任务',
      '/system-logs': '系统日志',
      '/profile': '个人设置',
    };

    // 动态路径处理
    if (path.startsWith('/roles/')) return '角色权限';
    return titleMap[path] || '';
  };

  // 获取订单页面标题
  const getOrdersTitle = (search: string) => {
    const params = new URLSearchParams(search);
    const service = params.get('service');

    return service ? ORDER_SERVICE_ACCESS_LABELS[service] || '业务订单' : '业务订单';
  };

  /**
   * 顶部用户信息优先显示角色中文名，避免仅展示用户名时看不出当前账号身份。
   */
  const get_current_user_role_labels = (): string[] => {
    const user_roles = Array.isArray((state.user as any)?.roles) ? (state.user as any).roles : [];

    if (user_roles.length > 0) {
      return Array.from(new Set(user_roles
        .map((role: any) => role?.display_name || role?.name)
        .filter(Boolean)));
    }

    return Array.from(new Set((state.roles || []).filter(Boolean)));
  };

  const current_user_role_labels = get_current_user_role_labels();

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人设置',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        width={256}
        style={{ height: '100vh', overflow: 'auto' }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {!collapsed && (
            <Title level={4} style={{ color: 'white', margin: 0 }}>
              {app_display_name}
            </Title>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>

      <Layout style={{ height: '100vh' }}>
        <Header
          style={{
            padding: 0,
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: 24,
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
            height: 56,
            lineHeight: '56px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 56,
                height: 56,
              }}
            />
            {getPageTitle() && (
              <Title level={4} style={{ margin: 0, marginLeft: 16 }}>
                {getPageTitle()}
              </Title>
            )}
          </div>

          <Space size="middle">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer', alignItems: 'center' }}>
                <Avatar
                  size="default"
                  src={(state.user as any)?.avatar || state.user?.wechat_avatar}
                  icon={<UserOutlined />}
                />
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span>{state.user?.display_name || (state.user as any)?.username || state.user?.email}</span>
                  {/*{current_user_role_labels.length > 0 && (*/}
                  {/*  <Text type="secondary" style={{ fontSize: 12 }}>*/}
                  {/*    {current_user_role_labels.join('、')}*/}
                  {/*  </Text>*/}
                  {/*)}*/}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: 0,
            background: '#f0f2f5',
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
